import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowRightLeft,
  CheckCircle,
  AlertCircle,
  X,
  Loader,
  AlertTriangle,
  SlidersHorizontal,
  Copy,
  Check,
  Play,
  Pause,
  Database,
  UploadCloud,
  FileSpreadsheet,
} from "lucide-react";
import api from "~/services/api";
import { formatter } from "~/functions";
import DateFilterPicker from "./DateFilterPicker";

const DATE_FILTER_OPTIONS = [
  { key: "due_date", label: "Due Date" },
  { key: "payment_date", label: "Payment Date" },
];

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(numeric) ? formatter.format(numeric) : value;
}

const STATUSES = ["Pending", "Migrated", "Error"];

const STATUS_BADGE = {
  Pending: "bg-yellow-100 text-yellow-700",
  Migrated: "bg-green-100 text-green-700",
  Error: "bg-red-100 text-red-700",
};

const RECEIVABLE_STATUS_BADGE = {
  Paid: "bg-green-100 text-green-700",
  Open: "bg-blue-100 text-blue-700",
  Overdue: "bg-red-100 text-red-700",
  Canceled: "bg-gray-100 text-gray-500",
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  );
}

function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

// ─── Error Cell (copyable) ─────────────────────────────────────────────────────

function ErrorCell({ message, mono = false }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(message || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-row items-center gap-2 max-w-sm">
      <span className={`truncate flex-1 ${mono ? "font-mono text-gray-600" : "text-red-600"}`} title={message}>
        {message || "—"}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        title="Copy"
        className={`shrink-0 flex items-center gap-1 rounded px-1.5 h-5 text-xs transition-all ${
          copied ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
        }`}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
}

// ─── Upload Panel ───────────────────────────────────────────────────────────────

function UploadPanel({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const inputRef = useRef();

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    setFile(f || null);
  }

  async function handleUpload() {
    if (!file) {
      toast("Select an Excel file first.", { type: "warning", autoClose: 2000 });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/receivables/migration-import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      toast("File uploaded and staged!", { type: "success", autoClose: 2500 });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onUploaded(data.batch_id);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to upload file.", { type: "error", autoClose: 3000 });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 border rounded-lg p-4 max-w-xl">
      <p className="text-sm font-semibold flex items-center gap-2">
        <FileSpreadsheet size={14} /> Upload Receivables Excel
      </p>
      <p className="text-xs text-gray-500">
        Upload the .xlsx file with legacy receivables. Rows are staged for review before migrating.
      </p>
      <div className="flex flex-row items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
          disabled={uploading}
          className="flex-1 border rounded-sm text-xs text-gray-600 file:mr-3 file:border-0 file:bg-secondary file:rounded-sm file:px-2 file:py-1.5 file:text-xs file:font-medium"
        />
        <button
          type="button"
          onClick={handleUpload}
          disabled={uploading || !file}
          className="flex items-center gap-2 border border-mila_orange text-mila_orange hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8 disabled:opacity-50 transition-all shrink-0"
        >
          {uploading ? <Loader size={12} className="animate-spin" /> : <UploadCloud size={12} />}
          {uploading ? "Uploading..." : "Upload"}
        </button>
      </div>

      {lastResult && (
        <div className="flex flex-row flex-wrap items-center gap-3 text-xs bg-gray-50 border rounded-md px-3 py-2 mt-1">
          <span className="text-gray-500">
            Batch: <span className="font-mono text-gray-700">{lastResult.batch_id}</span>
          </span>
          <span className="text-gray-500">
            Total: <span className="font-bold text-gray-700">{lastResult.total_rows}</span>
          </span>
          <span className="text-green-600">
            Staged: <span className="font-bold">{lastResult.staged}</span>
          </span>
          {lastResult.skipped > 0 && (
            <span className="text-amber-600">
              Skipped: <span className="font-bold">{lastResult.skipped}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Error Logs Modal ───────────────────────────────────────────────────────────

function ErrorLogsModal({ batchId, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regFilter, setRegFilter] = useState("");
  const [applied, setApplied] = useState("");

  async function fetchLogs(registration_number) {
    setLoading(true);
    try {
      const params = {};
      if (batchId) params.batch_id = batchId;
      if (registration_number) params.registration_number = registration_number;
      const { data } = await api.get("/receivables/migration-error-logs", { params });
      setLogs(data.logs || []);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to load error logs", { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchLogs("");
  }, []);

  function handleSearch() {
    setApplied(regFilter.trim());
    fetchLogs(regFilter.trim());
  }

  function handleClear() {
    setRegFilter("");
    setApplied("");
    fetchLogs("");
  }

  return (
    <div
      className="fixed z-50 bg-white shadow-lg border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "80%", maxWidth: "92%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-500" /> Migration Error Logs
          {!loading && <span className="ml-1 text-xs font-normal text-gray-500">— {logs.length} record(s)</span>}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex flex-row items-center gap-2 px-4 py-2 border-b shrink-0">
        <input
          type="text"
          value={regFilter}
          onChange={(e) => setRegFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Filter by Registration..."
          className="bg-secondary rounded-sm h-8 px-3 text-xs text-gray-600 focus:outline-none w-72"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="text-xs border border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white rounded-sm px-3 h-8 font-bold"
        >
          Filter
        </button>
        {applied && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs border rounded-sm px-3 h-8 text-gray-500 hover:bg-gray-100"
          >
            Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No error logs found.</p>
        ) : (
          <table className="w-full table-auto text-xs text-left whitespace-nowrap">
            <thead className="sticky top-0 bg-secondary">
              <tr className="h-8">
                <th className="px-4">Updated At</th>
                <th className="px-4">Row</th>
                <th className="px-4">Registration</th>
                <th className="px-4">Invoice</th>
                <th className="px-4">Status</th>
                <th className="px-4">Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-t odd:bg-white h-10">
                  <td className="px-4 text-gray-500">{formatDate(log.updated_at)}</td>
                  <td className="px-4 text-gray-500">{log.row_number ?? "—"}</td>
                  <td className="px-4 font-medium text-gray-700">{log.registration_number || "—"}</td>
                  <td className="px-4 text-gray-600">{log.source_invoice_number || "—"}</td>
                  <td className="px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[log.status] || "bg-gray-100 text-gray-500"}`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="px-4">
                    <ErrorCell message={log.error_message} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Row Detail (drill-down) ────────────────────────────────────────────────────

function RowDetail({ rowIds, onClose }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDetails() {
      try {
        const { data } = await api.post("/receivables/migration-result", { row_ids: rowIds });
        setResults(data.results || []);
      } catch (err) {
        toast(err?.response?.data?.error || "Failed to load migration result", { type: "error", autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, []);

  return (
    <div
      className="fixed z-50 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "62%", maxWidth: "84%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <ArrowRightLeft size={14} /> Migration Detail
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : !results?.length ? (
          <p className="text-sm text-gray-400 text-center py-12">No results found.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {results.map((r) => {
              const rec = r.receivable;
              return (
                <div key={r.id} className="border rounded-xl p-4 flex flex-col gap-3">
                  <div className="flex flex-row items-center flex-wrap gap-2 border-b pb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-500"}`}>
                      {r.status}
                    </span>
                    {rec && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RECEIVABLE_STATUS_BADGE[rec.status] || "bg-gray-100 text-gray-500"}`}>
                        {rec.status}
                      </span>
                    )}
                    {rec?.is_migrated && (
                      <span className="text-xs text-gray-400 italic ml-auto">Migrated invoice</span>
                    )}
                  </div>

                  {!rec ? (
                    <div className="text-xs text-gray-400 italic">Not migrated yet</div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Invoice Number</span>
                          <span className="text-sm font-bold text-gray-800">{rec.invoice_number}</span>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Old Invoice</span>
                          <span className="text-sm font-bold text-gray-800">{rec.old_invoice || "—"}</span>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Due Date</span>
                          <span className="text-sm font-bold text-gray-800">{formatShortDate(rec.due_date)}</span>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Amount</span>
                          <span className="text-sm font-bold text-gray-800">{formatMoney(rec.amount)}</span>
                        </div>
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-1">
                          <span className="text-xs font-medium text-gray-500">Balance</span>
                          <span className="text-sm font-bold text-gray-800">{formatMoney(rec.balance)}</span>
                        </div>
                      </div>

                      {rec.settlements?.length > 0 && (
                        <div className="flex flex-col gap-2">
                          <span className="text-xs font-bold text-gray-600">Settlements</span>
                          <table className="w-full table-auto text-xs text-left whitespace-nowrap border rounded-lg overflow-hidden">
                            <thead className="bg-secondary">
                              <tr className="h-8">
                                <th className="px-3">Amount</th>
                                <th className="px-3">Settlement Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rec.settlements.map((s) => (
                                <tr key={s.id} className="border-t odd:bg-white h-9">
                                  <td className="px-3 text-gray-700">{formatMoney(s.amount)}</td>
                                  <td className="px-3 text-gray-500">{formatShortDate(s.settlement_date)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Migrate All Modal ───────────────────────────────────────────────────────────

function MigrateAllModal({ batchId, onClose }) {
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);

  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errorRows, setErrorRows] = useState([]);

  const pausedRef = useRef(false);
  const nextOffsetRef = useRef(0);

  async function runLoop(offsetStart) {
    let offset = offsetStart;

    while (true) {
      if (pausedRef.current) break;

      const body = { batch_id: batchId, offset, batch_size: 50 };

      let data;
      try {
        ({ data } = await api.post("/receivables/migrate-all", body));
      } catch (err) {
        toast(err?.response?.data?.error || "Migrate All failed", { type: "error", autoClose: 3000 });
        setRunning(false);
        return;
      }

      setTotal(data.total);
      setProcessed(data.offset + data.processed_in_batch);
      setSuccessCount((s) => s + data.success_in_batch);
      setErrorCount((e) => e + data.error_in_batch);
      nextOffsetRef.current = data.next_offset;

      const errors = (data.results || []).filter((r) => r.status === "error");
      if (errors.length > 0) setErrorRows((prev) => [...prev, ...errors]);

      if (data.done) {
        setDone(true);
        setRunning(false);
        return;
      }

      offset = data.next_offset;
    }

    setRunning(false);
  }

  function handleStart() {
    nextOffsetRef.current = 0;
    setRunning(true);
    setPaused(false);
    setDone(false);
    setProcessed(0);
    setSuccessCount(0);
    setErrorCount(0);
    setErrorRows([]);
    setTotal(0);
    pausedRef.current = false;
    runLoop(0);
  }

  function handlePause() {
    pausedRef.current = true;
    setPaused(true);
    setRunning(false);
  }

  function handleResume() {
    pausedRef.current = false;
    setPaused(false);
    setRunning(true);
    runLoop(nextOffsetRef.current);
  }

  const progress = total > 0 ? Math.min((processed / total) * 100, 100) : 0;
  const canStart = !running && !paused && !done;

  return (
    <div
      className="fixed z-50 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden flex flex-col"
      style={{ width: "62%", maxWidth: "84%", height: "86%", top: "14%", right: 16 }}
    >
      <div className="flex flex-row items-center justify-between px-4 py-3 border-b bg-gray-50 shrink-0">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <Database size={14} /> Migrate All
          {batchId && <span className="ml-1 text-xs font-normal text-gray-500 font-mono">— {batchId}</span>}
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={running}
          className="font-bold bg-secondary rounded-md px-2 h-6 flex flex-row items-center justify-center text-xs gap-1 hover:bg-gray-200 disabled:opacity-40"
        >
          <X size={14} /> Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
        <div className="flex flex-row items-end gap-3 flex-wrap">
          <div className="flex flex-row gap-2 ml-auto">
            {canStart && (
              <button
                type="button"
                onClick={handleStart}
                className="flex items-center gap-2 border border-mila_orange text-mila_orange hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Play size={12} /> Start
              </button>
            )}
            {running && (
              <button
                type="button"
                onClick={handlePause}
                className="flex items-center gap-2 border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Pause size={12} /> Pause
              </button>
            )}
            {paused && (
              <button
                type="button"
                onClick={handleResume}
                className="flex items-center gap-2 border border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                <Play size={12} /> Resume
              </button>
            )}
            {(paused || done) && (
              <button
                type="button"
                onClick={handleStart}
                className="flex items-center gap-2 border border-gray-400 text-gray-500 hover:bg-gray-100 font-bold rounded-sm text-xs px-3 h-8 transition-all"
              >
                Restart
              </button>
            )}
          </div>
        </div>

        {(running || paused || done || processed > 0) && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center justify-between text-xs text-gray-500">
              <span>
                {running && <Loader size={11} className="inline animate-spin mr-1" />}
                {done ? "Done — " : paused ? "Paused — " : ""}
                <span className="font-bold text-gray-700">{processed}</span> / {total || "?"} processed
              </span>
              <span className="flex gap-3">
                <span className="text-green-600 font-bold">{successCount} success</span>
                <span className="text-red-500 font-bold">{errorCount} errors</span>
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${done ? "bg-green-500" : paused ? "bg-amber-400" : "bg-mila_orange"}`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {errorRows.length > 0 && (
          <div className="flex flex-col gap-2">
            <h3 className="text-xs font-bold text-red-500 flex items-center gap-1">
              <AlertTriangle size={12} /> Errors ({errorRows.length})
            </h3>
            <div className="overflow-y-auto max-h-80 border rounded-lg">
              <table className="w-full table-auto text-xs text-left whitespace-nowrap">
                <thead className="sticky top-0 bg-secondary">
                  <tr className="h-8">
                    <th className="px-4">Row ID</th>
                    <th className="px-4">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {errorRows.map((r, i) => (
                    <tr key={r.row_id || i} className="border-t odd:bg-white h-9">
                      <td className="px-4 font-mono text-gray-700">{r.row_id || "—"}</td>
                      <td className="px-4 text-red-500">{r.reason || r.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {done && errorRows.length === 0 && (
          <p className="text-sm text-green-600 font-medium text-center py-4">Migration completed with no errors.</p>
        )}
      </div>
    </div>
  );
}

// ─── Migration Report ────────────────────────────────────────────────────────────

function MigrationReport({ batchId, onMigrateAll, onErrorLogs, refreshKey }) {
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState("due_date");
  const [dateFilterMonth, setDateFilterMonth] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [detailIds, setDetailIds] = useState(null);
  const debounceRef = useRef(null);
  const limit = 100;

  async function fetchReport(newOffset, newStatus, newSearch, newBatchId, newDateFilterType, newDateFilterMonth) {
    setLoading(true);
    try {
      const params = {
        status: newStatus ?? statusFilter,
        offset: newOffset ?? offset,
        limit,
      };
      const batch = newBatchId !== undefined ? newBatchId : batchId;
      if (batch) params.batch_id = batch;
      const term = newSearch !== undefined ? newSearch : search;
      if (term.trim()) params.search = term.trim();
      const filterType = newDateFilterType !== undefined ? newDateFilterType : dateFilterType;
      const filterMonth = newDateFilterMonth !== undefined ? newDateFilterMonth : dateFilterMonth;
      if (filterMonth) params[`${filterType}_month`] = filterMonth;
      const { data } = await api.get("/receivables/migration-report", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to load migration report", { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchReport(0, statusFilter, "", batchId, dateFilterType, dateFilterMonth);
    setOffset(0);
    setSelectedIds(new Set());
  }, [batchId, refreshKey]);

  function handleStatusChange(s) {
    setStatusFilter(s);
    setOffset(0);
    setSelectedIds(new Set());
    fetchReport(0, s, search, undefined, dateFilterType, dateFilterMonth);
  }

  function handleSearchChange(value) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchReport(0, statusFilter, value, undefined, dateFilterType, dateFilterMonth);
    }, 400);
  }

  function handleDateFilterChange({ type, month }) {
    setDateFilterType(type);
    setDateFilterMonth(month);
    setOffset(0);
    setSelectedIds(new Set());
    fetchReport(0, statusFilter, search, undefined, type, month);
  }

  function handlePage(newOffset) {
    setOffset(newOffset);
    fetchReport(newOffset, statusFilter, search, undefined, dateFilterType, dateFilterMonth);
  }

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function toggleSelectAllVisible() {
    const eligible = rows.filter((r) => r.status !== "Migrated");
    const allSelected = eligible.length > 0 && eligible.every((r) => selectedIds.has(r.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligible.map((r) => r.id)));
    }
  }

  async function handleMigrate() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast("Select at least one row to migrate.", { type: "warning", autoClose: 2000 });
      return;
    }
    if (ids.length > 100) {
      toast("You can migrate a maximum of 100 rows at once.", { type: "warning", autoClose: 3000 });
      return;
    }
    setMigrating(true);
    try {
      const { data } = await api.post("/receivables/migrate", { row_ids: ids });
      const results = data?.results || [];
      const errors = results.filter((r) => r.status === "error");
      const successes = results.filter((r) => r.status === "success");
      const incompleteGroups = errors.filter((r) => r.incomplete_group);

      if (incompleteGroups.length > 0) {
        const missingRowNumbers = Array.from(
          new Set(incompleteGroups.flatMap((r) => r.missing_row_numbers || []))
        ).sort((a, b) => a - b);
        const missingRowIds = incompleteGroups.flatMap((r) => r.missing_row_ids || []);

        toast(
          `${incompleteGroups.length} invoice(s) need all their rows selected. Missing rows: ${missingRowNumbers.join(", ")}. They were added to your selection.`,
          { type: "warning", autoClose: 8000 }
        );
        setSelectedIds(new Set([...ids, ...missingRowIds]));
      } else {
        setSelectedIds(new Set());
      }

      const otherErrors = errors.filter((r) => !r.incomplete_group);
      if (otherErrors.length > 0) {
        toast(otherErrors[0].reason || "Some rows could not be migrated.", {
          type: "error",
          autoClose: 6000,
        });
      }

      if (successes.length > 0) {
        toast(`${successes.length} invoice(s) migrated from ${data.rows_touched} rows.`, {
          type: "success",
          autoClose: 3000,
        });
      } else if (errors.length === 0) {
        toast("Nothing to migrate.", { type: "warning", autoClose: 2000 });
      }

      setDetailIds(
        Array.from(new Set(results.flatMap((r) => r.group_row_ids || [r.row_id])))
      );
      await fetchReport(offset, statusFilter);
    } catch (err) {
      toast(err?.response?.data?.error || "Migration failed", { type: "error", autoClose: 3000 });
    } finally {
      setMigrating(false);
    }
  }

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const selectedCount = selectedIds.size;

  return (
    <div className="flex flex-1 flex-col w-full h-full overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-row items-center justify-between py-2 px-0 gap-2 h-12 shrink-0 flex-wrap">
        <div className="flex flex-row items-center gap-2 flex-wrap">
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={handleMigrate}
              disabled={migrating}
              className="flex flex-row items-center gap-2 border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white font-bold rounded-sm text-xs px-3 h-8 disabled:opacity-50"
            >
              {migrating ? <Loader size={12} className="animate-spin" /> : <ArrowRightLeft size={12} />}
              {migrating ? "Migrating..." : `Migrate Selected (${selectedCount})`}
            </button>
          )}
          <button
            type="button"
            onClick={onMigrateAll}
            disabled={!batchId}
            title={!batchId ? "Upload a file first" : undefined}
            className="flex flex-row items-center gap-2 border border-sky-600 text-sky-600 transition-all hover:bg-sky-600 hover:text-white font-bold rounded-sm text-xs px-3 h-8 disabled:opacity-40"
          >
            <Database size={12} /> Migrate All
          </button>
          <button
            type="button"
            onClick={onErrorLogs}
            className="flex flex-row items-center gap-2 border border-red-400 text-red-500 transition-all hover:bg-red-500 hover:text-white font-bold rounded-sm text-xs px-3 h-8"
          >
            <AlertTriangle size={12} /> Error Logs
          </button>
          <span className="text-xs text-gray-500">
            <span className="font-bold text-sky-700">{total}</span> rows
            {selectedCount > 0 && <span className="ml-2 font-bold text-mila_orange">{selectedCount} selected</span>}
          </span>
        </div>
        <div className="flex flex-row items-center gap-2">
          {totalPages > 1 && (
            <p className="text-xs text-gray-500 flex items-center gap-1">
              Page
              <select
                className="bg-secondary rounded-sm h-8 px-2 mx-1"
                value={currentPage}
                onChange={(e) => handlePage((Number(e.target.value) - 1) * limit)}
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              of <span className="font-bold text-sky-700 ml-1">{totalPages}</span>
            </p>
          )}
          <div className="flex items-center bg-secondary rounded-sm h-8 px-2 gap-2 min-w-36">
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search registration..."
              className="bg-transparent text-xs text-gray-500 focus:outline-none w-full"
            />
          </div>
          <DateFilterPicker
            type={dateFilterType}
            month={dateFilterMonth}
            onChange={handleDateFilterChange}
            options={DATE_FILTER_OPTIONS}
          />
          <div className="relative">
            <button
              type="button"
              onClick={() => setStatusPopoverOpen((o) => !o)}
              className={`flex flex-row items-center gap-2 rounded-sm h-8 px-2 transition ease-in-out whitespace-nowrap text-xs ${
                statusPopoverOpen ? "-translate-y-1 scale-110 bg-sky-700 text-white" : "bg-secondary text-gray-500 hover:bg-sky-700 hover:text-white"
              }`}
            >
              <SlidersHorizontal size={12} />
              {statusFilter}
            </button>
            {statusPopoverOpen && (
              <div className="absolute top-10 right-0 z-50 flex flex-col bg-secondary border border-gray-300 rounded-xl gap-1 shadow-xl p-2 text-xs">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      handleStatusChange(s);
                      setStatusPopoverOpen(false);
                    }}
                    className={`${statusFilter === s ? "bg-gray-300" : ""} hover:bg-gray-300 px-2 py-2 rounded text-left whitespace-nowrap`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => fetchReport(offset, statusFilter)}
            disabled={loading}
            className="text-xs border rounded-sm px-3 h-8 hover:bg-gray-100 text-gray-500 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
            <Loader size={20} className="animate-spin" />
            <span className="text-sm">Loading...</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">
            {batchId ? "No rows found." : "Upload an Excel file to see staged rows here."}
          </p>
        ) : (
          <table className="w-full table-auto text-xs bg-white rounded-xl overflow-hidden text-left whitespace-nowrap">
            <thead className="sticky top-0 bg-secondary">
              <tr className="h-8">
                <th className="px-4 w-10">
                  <input type="checkbox" className="cursor-pointer" onChange={toggleSelectAllVisible} />
                </th>
                <th className="px-4">Row</th>
                <th className="px-4">Registration</th>
                <th className="px-4">Source Invoice</th>
                <th className="px-4">Due Date</th>
                <th className="px-4">Status</th>
                <th className="px-4">Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isSelected = selectedIds.has(r.id);
                const isMigrated = r.status === "Migrated";
                return (
                  <tr
                    key={r.id}
                    className={`border-t h-10 cursor-pointer hover:border hover:border-mila_orange ${isSelected ? "bg-mila_orange text-white" : "odd:bg-white"}`}
                    onClick={() => setDetailIds([r.id])}
                  >
                    <td className="px-4 w-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isMigrated}
                        onChange={() => toggleSelect(r.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-4">{r.row_number}</td>
                    <td className="px-4 font-medium">{r.registration_number || "—"}</td>
                    <td className="px-4">{r.source_invoice_number || "—"}</td>
                    <td className="px-4">{formatShortDate(r.raw_row?.DueDate)}</td>
                    <td className="px-4">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          isSelected ? "bg-white/20 text-white" : STATUS_BADGE[r.status] || "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4">
                      {r.status === "Error" ? (
                        <span className={`truncate max-w-xs inline-block ${isSelected ? "text-white" : "text-red-600"}`} title={r.error_message}>
                          {r.error_message || "—"}
                        </span>
                      ) : (
                        <span className={isSelected ? "text-white/60" : "text-gray-300"}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Row Detail Overlay */}
      {detailIds && (
        <div className="fixed left-0 top-0 z-40 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <RowDetail rowIds={detailIds} onClose={() => setDetailIds(null)} />
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────────

export default function ReceivablesMigration() {
  const [batchId, setBatchId] = useState(null);
  const [errorLogsOpen, setErrorLogsOpen] = useState(false);
  const [migrateAllOpen, setMigrateAllOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  function handleUploaded(newBatchId) {
    setBatchId(newBatchId);
    setRefreshKey((k) => k + 1);
  }

  function handleMigrateAllClose() {
    setMigrateAllOpen(false);
    setRefreshKey((k) => k + 1);
  }

  return (
    <div className="flex flex-1 flex-col w-full h-full overflow-hidden gap-4">
      <UploadPanel onUploaded={handleUploaded} />

      <MigrationReport
        batchId={batchId}
        refreshKey={refreshKey}
        onMigrateAll={() => setMigrateAllOpen(true)}
        onErrorLogs={() => setErrorLogsOpen(true)}
      />

      {migrateAllOpen && (
        <div className="fixed left-0 top-0 z-50 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <MigrateAllModal batchId={batchId} onClose={handleMigrateAllClose} />
        </div>
      )}

      {errorLogsOpen && (
        <div className="fixed left-0 top-0 z-40 w-full h-full" style={{ background: "rgba(0,0,0,.2)" }}>
          <ErrorLogsModal batchId={batchId} onClose={() => setErrorLogsOpen(false)} />
        </div>
      )}
    </div>
  );
}
